<template>
  <!-- Full-screen filters of the river list: always visible next to the list (RiverFilterBar.vue on small screens) -->
  <aside class="river-filter-panel" aria-label="Filtres">
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="text-title-large font-weight-bold">Filtres</h2>
      <v-btn v-if="hasFilters" variant="text" size="small" color="primary" @click="clearAll">Tout effacer</v-btn>
    </div>

    <v-text-field
      :model-value="search"
      label="Rechercher une rivière"
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      density="comfortable"
      clearable
      hide-details
      class="mb-5"
      @update:model-value="$emit('update:search', $event || '')"
    ></v-text-field>

    <fieldset class="filter-group">
      <legend class="filter-legend">Afficher seulement</legend>
      <v-checkbox
        v-for="toggle in toggles"
        :key="toggle.key"
        v-model="criteria[toggle.key]"
        :label="toggle.label"
        :color="toggle.color"
        density="compact"
        hide-details
      ></v-checkbox>
    </fieldset>

    <fieldset class="filter-group">
      <legend class="filter-legend">Niveau actuel</legend>
      <v-chip-group v-model="criteria.levels" multiple column>
        <v-chip v-for="level in levels" :key="level.value" :value="level.value" filter variant="outlined" color="primary">
          <level-marker
            v-if="level.value !== 'unknown'"
            :status="level.value"
            :size="18"
            alt=""
            aria-hidden="true"
            class="mr-1"
          ></level-marker>
          <v-icon v-else icon="mdi-help-circle-outline" size="18" class="mr-1" aria-hidden="true"></v-icon>
          {{ level.label }}
        </v-chip>
      </v-chip-group>
    </fieldset>

    <fieldset class="filter-group">
      <legend class="filter-legend">Classe</legend>
      <v-chip-group v-model="criteria.classes" multiple column>
        <v-chip
          v-for="item in classes"
          :key="item.class_level"
          :value="item.class_level"
          filter
          variant="outlined"
          color="primary"
        >
          {{ item.class_level }}
        </v-chip>
      </v-chip-group>
    </fieldset>

    <fieldset class="filter-group">
      <legend class="filter-legend">
        Région<span v-if="criteria.regions.length"> · {{ criteria.regions.length }} choisie{{ criteria.regions.length > 1 ? "s" : "" }}</span>
      </legend>
      <v-checkbox
        v-for="region in regionItems"
        :key="region.name"
        v-model="criteria.regions"
        :value="region.name"
        color="primary"
        density="compact"
        hide-details
      >
        <template #label>
          <span class="flex-grow-1">{{ region.name }}</span>
          <span class="text-body-small text-medium-emphasis ml-2">{{ region.count }}</span>
        </template>
      </v-checkbox>
    </fieldset>
  </aside>
</template>

<script>
  import LevelMarker from "@/components/LevelMarker.vue";
  import { LEVEL_FILTERS, TOGGLE_FILTERS, activeFilters, clearCriteria } from "@/utils/riverFilters";

  export default {
    name: "RiverFilterPanel",
    components: { LevelMarker },
    props: {
      // Edited in place: RiverList.vue watches it and saves it to the user's profile
      criteria: { type: Object, required: true },
      search: { type: String, default: "" },
      regions: { type: Array, default: () => [] },
      classes: { type: Array, default: () => [] },
      regionCounts: { type: Object, default: () => ({}) },
    },
    emits: ["update:search"],
    data() {
      return { levels: LEVEL_FILTERS, toggles: TOGGLE_FILTERS };
    },
    computed: {
      regionItems() {
        // Regions without a published section would only give an empty list (kept while selected)
        return this.regions
          .filter(Boolean)
          .map((region) => ({ name: region.name, count: this.regionCounts[region.name] || 0 }))
          .filter((region) => region.count > 0 || this.criteria.regions.includes(region.name));
      },
      hasFilters() {
        return activeFilters(this.criteria).length > 0 || Boolean(this.search);
      },
    },
    methods: {
      clearAll() {
        clearCriteria(this.criteria);
        this.$emit("update:search", "");
      },
    },
  };
</script>

<style lang="scss" scoped>
  .river-filter-panel {
    flex: 0 0 300px;
    box-sizing: border-box;
    padding: 20px 20px 32px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    // Stays in view while the list scrolls, under the app bar
    position: sticky;
    top: var(--v-layout-top, 64px);
    align-self: flex-start;
    max-height: calc(100vh - var(--v-layout-top, 64px));
    overflow-y: auto;
  }
  .filter-group {
    border: 0;
    margin: 0 0 20px;
    padding: 0;
  }
  .filter-legend {
    padding: 0;
    margin-bottom: 4px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
  :deep(.v-checkbox .v-label) {
    width: 100%;
    opacity: 1;
  }
</style>
